import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Custom validators for Associates module
 */

// Name validator: only alphabets and spaces, 2-100 characters
export const nameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  if (value.length < 2 || value.length > 100) {
    return { nameLength: { min: 2, max: 100, actual: value.length } };
  }
  
  // Only alphabets and spaces allowed
  const namePattern = /^[a-zA-Z\s]+$/;
  if (!namePattern.test(value)) {
    return { namePattern: true };
  }
  
  return null;
};

// About College validator: 10-1000 characters, specific characters allowed
export const aboutCollegeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  if (value.length < 10 || value.length > 1000) {
    return { aboutCollegeLength: { min: 10, max: 1000, actual: value.length } };
  }
  
  // Allow: Alphabets, Numbers, Spaces, Common punctuation (. , - / ( ) &)
  const allowedPattern = /^[a-zA-Z0-9\s.,\-/()&]+$/;
  if (!allowedPattern.test(value)) {
    return { aboutCollegePattern: true };
  }
  
  return null;
};

// Address validator: 5-255 characters, specific characters allowed
export const addressValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  if (value.length < 5 || value.length > 255) {
    return { addressLength: { min: 5, max: 255, actual: value.length } };
  }
  
  // Allow: Alphabets, Numbers, Spaces, , . - / #
  const allowedPattern = /^[a-zA-Z0-9\s.,\-/#]+$/;
  if (!allowedPattern.test(value)) {
    return { addressPattern: true };
  }
  
  return null;
};

// Indian mobile number validator: exactly 10 digits, first digit 6-9
export const indianMobileValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  // Only digits allowed
  const digitsOnly = /^\d+$/;
  if (!digitsOnly.test(value)) {
    return { contactDigitsOnly: true };
  }
  
  // Exactly 10 digits
  if (value.length !== 10) {
    return { contactLength: { expected: 10, actual: value.length } };
  }
  
 
  
  return null;
};

// Email validator with max length
export const emailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  // No spaces allowed
  if (/\s/.test(value)) {
    return { emailNoSpaces: true };
  }
  
  // Max 100 characters
  if (value.length > 100) {
    return { emailMaxLength: { max: 100, actual: value.length } };
  }
  
  // Valid email format
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailPattern.test(value)) {
    return { emailFormat: true };
  }
  
  return null;
};

// Username validator: 5-30 characters, alphanumeric, underscore, period, no spaces, unique
export const usernameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  if (value.length < 5 || value.length > 30) {
    return { usernameLength: { min: 5, max: 30, actual: value.length } };
  }
  
  // Allow: Alphabets, Numbers, Underscore (_), Period (.)
  const usernamePattern = /^[a-zA-Z0-9_.]+$/;
  if (!usernamePattern.test(value)) {
    return { usernamePattern: true };
  }
  
  // No spaces
  if (/\s/.test(value)) {
    return { usernameNoSpaces: true };
  }
  
  return null;
};

// Password validator: 8-20 characters, must contain uppercase, lowercase, digit, special char
export const passwordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value);
  
  if (value.length < 8 || value.length > 20) {
    return { passwordLength: { min: 8, max: 20, actual: value.length } };
  }
  
  // No spaces allowed
  if (/\s/.test(value)) {
    return { passwordNoSpaces: true };
  }
  
  const errors: any = {};
  
  // At least 1 uppercase letter
  if (!/[A-Z]/.test(value)) {
    errors.passwordUppercase = true;
  }
  
  // At least 1 lowercase letter
  if (!/[a-z]/.test(value)) {
    errors.passwordLowercase = true;
  }
  
  // At least 1 digit
  if (!/[0-9]/.test(value)) {
    errors.passwordDigit = true;
  }
  
  // At least 1 special character (@ # $ % & * !)
  if (!/[@#$%&*!]/.test(value)) {
    errors.passwordSpecialChar = true;
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

// Designation validator: only alphabets and spaces, 2-100 characters
export const designationValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const value = String(control.value).trim();
  
  if (value.length < 2 || value.length > 100) {
    return { designationLength: { min: 2, max: 100, actual: value.length } };
  }
  
  // Only alphabets and spaces allowed
  const designationPattern = /^[a-zA-Z\s]+$/;
  if (!designationPattern.test(value)) {
    return { designationPattern: true };
  }
  
  return null;
};

// Institute Name validator: max 255 characters, only when white labelled is enabled
export const instituteNameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }

  const value = String(control.value).trim();

  // Required (when used standalone; FormControl might not always have Validators.required)
  if (value.length === 0) {
    return { instituteNameRequired: true };
  }

  // Max length
  if (value.length > 255) {
    return { instituteNameMaxLength: { max: 255, actual: value.length } };
  }

  // Min length
  if (value.length < 2) {
    return { instituteNameMinLength: { min: 2, actual: value.length } };
  }

  return null;
};
